import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import PageContainer from '../../components/PageElements/PageContainer/PageContainer';
import DietaryTab from './tabs/DietaryTab';
import MyIdTab from './tabs/MyIdTab';
import IngredientsTab from './tabs/IngredientsTab';
import FavoritesTab from './tabs/FavoritesTab';
import PrivacyTab from './tabs/PrivacyTab';
import AccountTab from './tabs/AccountTab';
import '../../constants/theme.css';
import styles from './Settings.module.css';
import { Notification } from '../../components/Notification/Notification';

type TabType = 'dietary' | 'my-id' | 'ingredients' | 'favorites' | 'privacy' | 'account';

const Settings: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('dietary');
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dietary':
        return <DietaryTab onNotification={handleNotification} />;
      case 'ingredients':
        return <IngredientsTab onNotification={handleNotification} />;
      case 'favorites':
        return <FavoritesTab onNotification={handleNotification} />;
      case 'my-id':
        return <MyIdTab />;
      case 'privacy':
        return <PrivacyTab />;
      case 'account':
        return <AccountTab onNotification={handleNotification} />;
      default:
        return null;
    }
  };

  const tabs = [
    { id: 'dietary', label: 'Dietary' },
    { id: 'favorites', label: 'Favorites' },
    { id: 'ingredients', label: 'Ingredients' },
    { id: 'my-id', label: 'My ID' },
    { id: 'privacy', label: 'Privacy' },
    { id: 'account', label: 'Account' }
  ];

  return (
    <PageContainer>
      <Header user={user} onLogout={logout} />
      <div className={styles.container}>
        <div className={styles.sidebar}>
          <div className={styles.sidebarNav}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`${styles.sidebarTab} ${activeTab === tab.id ? styles.activeTab : ''}`}
                onClick={() => setActiveTab(tab.id as TabType)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.mainContent}>
          {renderTabContent()}
        </div>
      </div>

      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </PageContainer>
  );
};

export default Settings;